import { CheckCircle2, XCircle } from 'lucide-react'
import { useEffect } from 'react'
import { api } from '../api'
import { useApp } from '../context'
import { budgetVerdict, fmtDb, fmtDbm, fmtM } from '../format'
import { useAsync } from '../hooks'
import { PanelShell } from './PanelShell'
import { Callout, ErrorState, KeyValue, Loading, Section } from './ui'

/** "Is this port lit?" — the OSP team's check after building or splicing, before connecting a customer. */
export function PortPathPanel({ splitterId, port, nodeId }: { splitterId: string; port: number; nodeId: string }) {
  const { setHighlight, open, nodeName } = useApp()
  const q = useAsync(() => api.portPath(splitterId, port), [splitterId, port])
  useEffect(() => {
    if (q.data) setHighlight({ cableIds: q.data.cableIds, nodeIds: [nodeId], tone: q.data.reachesOlt ? 'accent' : 'danger' })
  }, [q.data, nodeId, setHighlight])
  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Loading label="Tracing to the OLT…" />
  const r = q.data
  const v = budgetVerdict(r.budget)
  const total = r.steps.at(-1)?.cumulativeLengthM ?? 0
  return (
    <PanelShell title={`${nodeName(nodeId)} port ${port}`} subtitle="Path from this splitter port back to the OLT">
      <Section title="Result">
        {r.reachesOlt ? (
          <Callout tone={v.tone === 'ok' ? 'ok' : v.tone} title={`Lit from ${r.olt}`}>
            {r.budget && (
              <KeyValue
                rows={[
                  ['Loss to this port', fmtDb(r.budget.totalLossDb)],
                  ['Expected light here', fmtDbm(r.budget.expectedRxDbm)],
                  ['Margin before the drop', fmtDb(r.budget.marginDb)],
                  ['Fibre length', fmtM(total)],
                ]}
              />
            )}
            <p className="mt-2 text-xs">A drop cable and ONU will add roughly 0.6–0.7 dB.</p>
          </Callout>
        ) : (
          <Callout tone="danger" title="Not connected to an OLT yet">
            The fibre stops at <strong>{r.end.nodeName}</strong>: {r.end.at} has no splice. Record the missing splice there.
            {r.end.nodeId && (
              <button className="mt-2 block font-medium text-accent underline" onClick={() => open({ kind: 'node', id: r.end.nodeId! })}>
                Open {r.end.nodeName}
              </button>
            )}
          </Callout>
        )}
      </Section>
      <Section title={`Path · ${r.steps.length} steps`}>
        <ol className="space-y-1.5 text-sm">
          {r.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              {s.fiberColor ? (
                <span className="mt-1 h-3 w-3 shrink-0 rounded-full border border-black/20" style={{ background: s.fiberColor, boxShadow: `0 0 0 2px ${s.tubeColor}` }} aria-hidden />
              ) : i === 0 && r.reachesOlt ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden />
              ) : (
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-border" aria-hidden />
              )}
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{s.title}</span>
                <span className="block break-words text-xs text-muted">{s.detail}</span>
              </span>
              <span className="num shrink-0 text-xs text-muted">Σ {s.cumulativeLossDb.toFixed(2)} dB</span>
            </li>
          ))}
          {!r.reachesOlt && (
            <li className="flex items-center gap-2 font-medium text-danger">
              <XCircle className="h-4 w-4" aria-hidden /> Fibre ends here: missing splice
            </li>
          )}
        </ol>
      </Section>
    </PanelShell>
  )
}
